import { useEffect, useMemo, useState } from 'react';

import { effectiveAnchor } from './procedural-helpers';
import { ReviewCard } from './procedural-review-card';
import {
  chunkToSourceRef,
  ProceduralSourcePanelBody,
  ProceduralSourceRef,
  SourceCitation,
  sourceRefFromChunks,
} from './procedural-source-drawer';
import type { ProceduralTimelineEventSuggestion } from '../../../../../../../../backend/src/matter/procedural-timeline/procedural-timeline-event-suggestion.entity';
import type { ProceduralTimelineEvent } from '../../../../../../../../backend/src/matter/procedural-timeline/procedural-timeline-event.entity';
import { Icon } from '@/new/components/icon';
import {
  ACTIVE_HIGHLIGHT_COLOR,
  HIGHLIGHT_COLOR,
  ORIGINAL_HIGHLIGHT_COLOR,
} from '@/new/components/pdf-viewer/constants';
import { Button } from '@/new/components/shadcn/button';
import { Dialog, DialogContent, DialogDescription, DialogTitle } from '@/new/components/shadcn/dialog';
import { Progress } from '@/new/components/shadcn/progress';

interface ProceduralReviewModalProps {
  open: boolean;
  onOpenChange: (open: boolean) => void;
  /** The matter's pending suggestions (live — the queue shrinks as they resolve). */
  suggestions: ProceduralTimelineEventSuggestion[];
  eventsById: Record<string, ProceduralTimelineEvent>;
  matterId: string;
}

export function ProceduralReviewModal({
  open,
  onOpenChange,
  suggestions,
  eventsById,
  matterId,
}: ProceduralReviewModalProps) {
  // Reviewable first (blocked = waiting on another pending suggestion), then
  // creation order within each tier (document order within a batch).
  const queue = useMemo(() => {
    const reviewable = suggestions.filter((s) => s.proposedAnchorSuggestionId === null);
    const blocked = suggestions.filter((s) => s.proposedAnchorSuggestionId !== null);
    reviewable.sort(compareForReview);
    blocked.sort(compareForReview);
    return [...reviewable, ...blocked];
  }, [suggestions]);

  // Pin the card to one suggestion so a background refetch (e.g. rejecting a
  // parent unblocks a dependent, re-sorting the queue) can't yank the reviewer
  // to a different one. Advance only when the pinned suggestion leaves the
  // queue — i.e. the reviewer resolved it.
  const [currentId, setCurrentId] = useState<string | null>(null);
  const current = (currentId !== null ? queue.find((s) => s.id === currentId) : undefined) ?? queue[0] ?? null;
  useEffect(() => {
    if (current?.id !== currentId) setCurrentId(current?.id ?? null);
  }, [current, currentId]);

  // Freeze the starting total so the bar fills as the queue drains.
  const [total, setTotal] = useState(0);
  useEffect(() => {
    if (open) {
      setTotal(suggestions.length);
      setCurrentId(null);
    }
  }, [open]);
  const decided = Math.max(total - queue.length, 0);
  const progress = total > 0 ? (decided / total) * 100 : 100;

  const dependentCountById = useMemo(() => {
    const m: Record<string, number> = {};
    for (const s of suggestions) {
      if (s.proposedAnchorSuggestionId !== null) {
        m[s.proposedAnchorSuggestionId] = (m[s.proposedAnchorSuggestionId] ?? 0) + 1;
      }
    }
    return m;
  }, [suggestions]);

  // The suggestion's own citation, if it has one.
  const ownSourceRef = useMemo(() => (current !== null ? chunkToSourceRef(current.chunk) : null), [current]);

  // The anchor event's citation: a second highlight, or the primary when the
  // suggestion cites none of its own (common for linked dates).
  const anchorSourceRef = useMemo(() => {
    if (current === null) return null;
    const anchorEventId = effectiveAnchor(current).anchorEventId;
    if (anchorEventId === null) return null;
    return sourceRefFromChunks(eventsById[anchorEventId]?.sourceChunks, ownSourceRef?.fileId);
  }, [current, eventsById, ownSourceRef]);

  // For an Update, the existing event's own citation (distinct from the new
  // evidence and the anchor).
  const originalSourceRef = useMemo(() => {
    if (current === null || current.kind !== 'update' || current.existingEventId === null) return null;
    return sourceRefFromChunks(eventsById[current.existingEventId]?.sourceChunks, ownSourceRef?.fileId);
  }, [current, eventsById, ownSourceRef]);

  // Panel citations, deduped by chunk and colour-coded by role; the
  // suggestion's own source leads when present.
  const citations = useMemo<SourceCitation[]>(() => {
    const list: SourceCitation[] = [];
    const seen = new Set<string>();
    const add = (ref: ProceduralSourceRef | null, label: string, color: string) => {
      if (ref === null || seen.has(ref.chunkId)) return;
      seen.add(ref.chunkId);
      list.push({ ref, label, color });
    };
    const ownLabel = current?.kind === 'update' ? 'Updated source' : 'Suggested source';
    add(ownSourceRef, ownLabel, ACTIVE_HIGHLIGHT_COLOR);
    add(originalSourceRef, 'Original source', ORIGINAL_HIGHLIGHT_COLOR);
    add(anchorSourceRef, 'Dependent source', HIGHLIGHT_COLOR);
    return list;
  }, [current, ownSourceRef, originalSourceRef, anchorSourceRef]);

  return (
    <Dialog open={open} onOpenChange={onOpenChange}>
      <DialogContent className="flex h-[90vh] w-[96vw] max-w-[1600px] flex-col overflow-hidden p-0">
        <div className="shrink-0 border-b border-gray-200 bg-gray-50 px-6 py-5 pb-3.5">
          <DialogTitle>Review suggestions</DialogTitle>
          <DialogDescription>Confirm and edit each suggestion against its source document.</DialogDescription>

          <div className="flex items-center gap-3 pt-2">
            <Progress value={progress} className="h-1.5" />
            <span className="shrink-0 text-xs text-gray-500">
              {decided} of {total} reviewed
            </span>
          </div>
        </div>

        <div className="flex min-h-0 flex-1">
          {current !== null ? (
            <>
              <div className="m-4 mr-0 flex min-w-0 flex-1 flex-col overflow-hidden rounded-lg border">
                {citations.length > 0 ? (
                  <ProceduralSourcePanelBody citations={citations} dismissable={false} onClose={() => undefined} />
                ) : (
                  <div className="flex h-full items-center justify-center p-6 text-center text-sm text-gray-400">
                    No source document to show.
                  </div>
                )}
              </div>

              <div className="flex h-full w-xl shrink-0 flex-col p-4">
                <ReviewCard
                  key={current.id}
                  suggestion={current}
                  eventsById={eventsById}
                  matterId={matterId}
                  dependentSuggestionCount={dependentCountById[current.id] ?? 0}
                />
              </div>
            </>
          ) : (
            <div className="flex flex-1 flex-col p-4">
              <ReviewFinishedScreen onClose={() => onOpenChange(false)} />
            </div>
          )}
        </div>
      </DialogContent>
    </Dialog>
  );
}

function ReviewFinishedScreen({ onClose }: { onClose: () => void }) {
  return (
    <div className="flex h-full flex-col items-center justify-center gap-3 text-center">
      <Icon className="lucide--check-check text-3xl text-teal-500" />
      <p className="text-lg font-medium text-gray-900">All caught up</p>
      <p className="max-w-sm text-sm text-gray-500">Every suggestion has been reviewed.</p>
      <Button variant="primary" onClick={onClose} className="mt-2">
        Done
      </Button>
    </div>
  );
}

// First created first (an extraction batch shares one createdAt and can't be
// started while suggestions are pending, so it always sorts ahead of the later
// cascade edits), then document reading order within a batch; citation-less last.
function compareForReview(a: ProceduralTimelineEventSuggestion, b: ProceduralTimelineEventSuggestion): number {
  const ka = appearanceKey(a);
  const kb = appearanceKey(b);
  if (ka.createdAt !== kb.createdAt) return ka.createdAt - kb.createdAt;
  if (ka.page !== kb.page) return ka.page - kb.page;
  if (ka.top !== kb.top) return ka.top - kb.top;
  if (ka.left !== kb.left) return ka.left - kb.left;
  return ka.id.localeCompare(kb.id);
}

// Where the suggestion sits in its source doc (page, then top-to-bottom,
// left-to-right), with createdAt/id as stable tie-breakers.
function appearanceKey(s: ProceduralTimelineEventSuggestion) {
  const box = s.chunk?.boundingBox ?? null;
  return {
    page: s.chunk?.pageNumber ?? Number.POSITIVE_INFINITY,
    top: box?.top ?? Number.POSITIVE_INFINITY,
    left: box?.left ?? Number.POSITIVE_INFINITY,
    createdAt: new Date(s.createdAt).getTime(),
    id: s.id,
  };
}
